/**
 * MyPreferencesPage — per-user preferences route.
 * Avatar, display name, timezone, language, email digest, notification toggles.
 */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Upload, User, Bell, Globe, Image as ImageIcon } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { uploadImage } from '../utils/uploadImage';
import TrustedDevicesSection from '../components/auth/TrustedDevicesSection';

const TIMEZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Europe/London', 'Europe/Berlin', 'Europe/Paris',
  'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo', 'Asia/Shanghai',
  'Australia/Sydney',
];
const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'pt', label: 'Português' },
  { code: 'ar', label: 'العربية' },
  { code: 'hi', label: 'हिंदी' },
];
const NOTIF_KEYS = [
  { key: 'email_module_assigned', label: 'New module assigned to me', channel: 'Email' },
  { key: 'email_quiz_graded', label: 'My quiz was graded', channel: 'Email' },
  { key: 'email_weekly_digest', label: 'Weekly progress digest', channel: 'Email' },
  { key: 'inapp_module_assigned', label: 'New module assigned to me', channel: 'In-app' },
  { key: 'inapp_quiz_graded', label: 'My quiz was graded', channel: 'In-app' },
];

export default function MyPreferencesPage() {
  const { get, put } = useApi();
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [form, setForm] = useState({
    avatar_url: null, display_name: '', timezone: 'UTC', language: 'en',
    email_digest: 'weekly',
    notifications: {
      email_module_assigned: true, email_quiz_graded: true,
      email_weekly_digest: false,
      inapp_module_assigned: true, inapp_quiz_graded: true,
    },
  });

  useEffect(() => {
    get('/preferences/me')
      .then(p => setForm(prev => ({ ...prev, ...p })))
      .catch(e => toast.error(e.response?.data?.detail || 'Failed to load preferences'))
      .finally(() => setLoading(false));
  }, [get]);

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const updateNotif = (key, value) =>
    setForm(prev => ({ ...prev, notifications: { ...prev.notifications, [key]: value } }));

  const handleAvatar = async (file) => {
    setUploadingAvatar(true);
    try {
      const res = await uploadImage(file, 'avatar');
      update('avatar_url', res.url);
      toast.success('Avatar uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await put('/preferences/me', form);
      toast.success('Preferences saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="rounded-lg border p-10 text-center text-muted-foreground">Loading preferences…</div>;
  }

  return (
    <div className="space-y-6 pb-24 max-w-3xl relative" data-testid="my-preferences-page">
      <div aria-hidden className="page-blob bg-[var(--lumyn-settings)]" />
      <div className="relative z-10 space-y-6">
        <div className="space-y-1.5">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[var(--lumyn-settings)]/10 text-[var(--lumyn-settings)] text-[11px] font-semibold uppercase tracking-[0.14em]">
            <User className="w-3.5 h-3.5" />{'Preferences'}
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{'My preferences'}</h1>
          <p className="text-sm text-muted-foreground">{'Personalize how Lumyn looks and notifies you.'}</p>
        </div>

      {/* Avatar */}
      <section className="rounded-xl border p-5 space-y-4 bg-card">
        <h2 className="font-display font-semibold flex items-center gap-2"><ImageIcon className="w-4 h-4 text-[var(--lumyn-brand)]" />{'Avatar'}</h2>
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-full bg-gold-100 text-gold-800 flex items-center justify-center text-xl font-semibold overflow-hidden ring-1 ring-gold/40">
            {form.avatar_url
              ? <img src={form.avatar_url} alt="avatar" className="w-full h-full object-cover" />
              : ((user?.first_name?.[0] || '') + (user?.last_name?.[0] || '')) || '?'}
          </div>
          <div className="space-y-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && handleAvatar(e.target.files[0])}
              data-testid="avatar-file-input"
            />
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploadingAvatar} data-testid="avatar-upload-btn">
                {uploadingAvatar ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {form.avatar_url ? 'Replace' : 'Upload'}
              </Button>
              {form.avatar_url && (
                <Button size="sm" variant="ghost" onClick={() => update('avatar_url', null)} data-testid="avatar-clear-btn">Remove</Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">PNG / JPG / WebP, square works best. Max 5 MB.</p>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="display-name">Display name</Label>
          <Input
            id="display-name"
            value={form.display_name || ''}
            onChange={(e) => update('display_name', e.target.value)}
            maxLength={80}
            placeholder={`${user?.first_name || ''} ${user?.last_name || ''}`.trim()}
            data-testid="display-name-input"
          />
          <p className="text-xs text-muted-foreground">Shown next to your avatar. Defaults to your full name.</p>
        </div>
      </section>

      {/* Locale */}
      <section className="rounded-xl border p-5 space-y-4 bg-card">
        <h2 className="font-display font-semibold flex items-center gap-2"><Globe className="w-4 h-4 text-[var(--lumyn-settings)]" />{'Locale'}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>{'Timezone'}</Label>
            <Select value={form.timezone} onValueChange={(v) => update('timezone', v)}>
              <SelectTrigger data-testid="timezone-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIMEZONES.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{'Language'}</Label>
            <Select value={form.language} onValueChange={(v) => update('language', v)}>
              <SelectTrigger data-testid="language-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LANGS.map(l => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Notifications */}
      <section className="rounded-xl border p-5 space-y-4 bg-card">
        <h2 className="font-display font-semibold flex items-center gap-2"><Bell className="w-4 h-4 text-[var(--lumyn-settings)]" />{'Notifications'}</h2>

        <div className="space-y-1.5">
          <Label>Email digest cadence</Label>
          <Select value={form.email_digest} onValueChange={(v) => update('email_digest', v)}>
            <SelectTrigger data-testid="email-digest-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="never">Never</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 pt-2">
          {NOTIF_KEYS.map(n => (
            <div key={n.key} className="flex items-center justify-between py-2 border-t first:border-t-0">
              <div>
                <p className="text-sm font-medium">{n.label}</p>
                <p className="text-xs text-muted-foreground">{n.channel}</p>
              </div>
              <Switch
                checked={!!form.notifications[n.key]}
                onCheckedChange={(v) => updateNotif(n.key, v)}
                data-testid={`notif-${n.key}`}
              />
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-xl border p-5 space-y-4 bg-card">
        <TrustedDevicesSection />
      </section>

      <div className="flex justify-end sticky bottom-4 z-10">
        <Button size="lg" onClick={save} disabled={saving} data-testid="preferences-save-btn">
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {'Save preferences'}
        </Button>
      </div>
      </div>
    </div>
  );
}
